import { createSlice, createEntityAdapter } from '@reduxjs/toolkit';
import { isJson, clearStringValue } from 'Core/data/Helpers';
import { getApiDate, validateFutureDates, convertEpochToUTC } from '../../hooks/useUnitData';
import { LogManagerConsole } from '../../logManager';

// Helpers migrated from the old LastReading processor
const getAddress = (address) => {
    if (address) {
        if (typeof address === 'string') {
            let n = 4;
            const parsedAddress = address.replace(/=/g, '":"').replace('{', '{"').replace('}', '"}').replace(/,/g, s => n ? (n--, '","') : s).replace(/\s/g, '').replace(/'/g, '"').replace(/""/g, '"');
            address = isJson(parsedAddress) && JSON.parse(parsedAddress) || parsedAddress;
        }
    }
    return address;
};

const getTemperatures = (temperatures) => {
    if (!temperatures) return temperatures;
    if (typeof temperatures !== 'string') return temperatures;

    temperatures = temperatures.replace(/=/gi, ":");
    let parsedString = temperatures.replace(/(['"])?([a-z0-9A-Z_]+)(['"])?:/g, '"$2": ');

    return isJson(parsedString) && JSON.parse(parsedString) || {};
};

const getDesiredStarterState = (desiredStarterState) => {
    if (desiredStarterState) {
        if (typeof desiredStarterState === 'string' && desiredStarterState.indexOf('{') !== -1) {
            const parsedState = desiredStarterState.replace(/=/g, '":"').replace('{', '{"').replace('}', '"}').replace(/,/g, '","').replace(/\s/g, '').replace(/'/g, '"').replace(/""/g, '"');
            if (isJson(parsedState)) {
                const stateAsObject = JSON.parse(parsedState);
                if (stateAsObject.state === "true" || stateAsObject.state === "Enable") {
                    desiredStarterState = "Enable";
                } else if (stateAsObject.state === "false" || stateAsObject.state === "Disable") {
                    desiredStarterState = "Disable";
                }
            }
        }
    }
    return desiredStarterState;
};

const validateIgnitionOff = (eventCode, eventName) => {
    return !(eventName.toLowerCase() === 'travel stop' &&
        (eventCode == '5' || eventCode == '23' || eventCode == '-SN' || eventCode == '-SF' ||
            eventCode == '6031' || eventCode == '12' || eventCode == '301' || eventCode == '54'));
};

const validateLastEvent = (eventName, oldEventName, eventCode) => {
    if (oldEventName && oldEventName.toLowerCase() === 'ignition off') {
        return validateIgnitionOff(eventCode, eventName);
    }
    return true;
};

// Main RTK Entity Adapter Configuration
export const lastReadingAdapter = createEntityAdapter({
    // Utilize the `deviceId` (or `id`) as the primary key in the dictionary
    selectId: (reading) => reading.deviceId || reading.id,
    // Optional: Sort logic could be defined here using `sortComparer` if we wanted alphabetical order by default
});

const lastReadingSlice = createSlice({
    name: 'realtime_lastReading',
    initialState: lastReadingAdapter.getInitialState(),
    reducers: {
        /**
         * Custom upsert many that applies the business rules exactly like the old processor,
         * but updates only the specifically mutated objects, avoiding the clone of the entire array.
         */
        upsertManyInternal: (state, action) => {
            const items = action.payload; // Array of socket payload items

            LogManagerConsole("RT_DEBUG", "[LastReadingSlice] upsertManyInternal dispatched incoming size:", items ? items.length : 0);

            if (!items || !items.length) return;

            const validUpdates = [];

            for (const newItemRaw of items) {
                const deviceId = newItemRaw.deviceId || newItemRaw.id;

                // For `update` scenarios, extract the currently stored value in O(1) time
                const existingItem = state.entities[deviceId] || {};

                let newItem = {
                    ...newItemRaw,
                    serverTime: getApiDate(newItemRaw.serverTime),
                    unitTime: getApiDate(newItemRaw.unitTime)
                };

                let stringToLog = {
                    ageSeconds: (newItem.unitTime || 0) - (existingItem.unitTime || 0),
                    unitTimeOld: convertEpochToUTC(existingItem.unitTime),
                    unitTime: convertEpochToUTC(newItem.unitTime),
                    serverTime: convertEpochToUTC(newItem.serverTime),
                    unitId: existingItem.id,
                    deviceId: newItem.deviceId,
                    rowId: newItem?.rowId,
                    Label: newItem.deviceLabel,
                    eventName: newItem.eventName,
                    eventCode: newItem?.eventCode
                };

                // VALIDATIONS
                if (!newItem.ignoreValidation) {
                    if (newItem.unitTime && existingItem.unitTime > newItem.unitTime) {
                        LogManagerConsole("LastReading-Rejected", "**** Rejected Socket Reading: (unitTime)", stringToLog);
                        continue; // skip older item
                    }
                    if (!validateFutureDates(newItem.unitTime)) {
                        LogManagerConsole("LastReading-Rejected", "**** Rejected Socket Reading: (validateFutureDates)", stringToLog);
                        continue; // skip future item
                    }
                    if (newItem.unitTime && newItem.unitTime == existingItem.unitTime && !validateLastEvent(newItem.eventName, existingItem.eventName, newItem.eventCode)) {
                        LogManagerConsole("LastReading-Rejected", "**** Rejected Socket Reading: (validateLastEvent)", stringToLog);
                        continue; // skip unallowed duplicated time event
                    }
                }

                // MERGE & TRANSFORM
                const latitude = newItem.latitude !== undefined ? newItem.latitude : existingItem.latitude;
                const longitude = newItem.longitude !== undefined ? newItem.longitude : existingItem.longitude;
                const speed = newItem.speed !== undefined ? newItem.speed : existingItem.speed;
                const ecuSpeed = newItem.ecuSpeed !== undefined ? newItem.ecuSpeed : existingItem.ecuSpeed;
                const haveLatLng = Boolean((latitude && !isNaN(latitude) && parseFloat(latitude) !== 0) && (longitude && !isNaN(longitude) && parseFloat(longitude) !== 0)) || newItem.haveLatLng;

                let mergedItem = {
                    ...existingItem,
                    id: Number(deviceId), // Ensure the internal target ID is populated
                    deviceId: deviceId,
                    make: newItem.make !== undefined ? clearStringValue(newItem.make) : clearStringValue(existingItem.make),
                    model: newItem.model !== undefined ? clearStringValue(newItem.model) : clearStringValue(existingItem.model),
                    year: newItem.vin !== undefined ? clearStringValue(newItem.year) : clearStringValue(existingItem.year),
                    vin: newItem.vin !== undefined ? clearStringValue(newItem.vin) : clearStringValue(existingItem.vin),
                    ecuVin: newItem.ecuVin !== undefined ? newItem.ecuVin : existingItem.ecuVin,
                    unitTime: newItem.unitTime !== undefined ? newItem.unitTime : existingItem.unitTime,
                    eventCode: newItem.eventCode !== undefined ? newItem.eventCode : existingItem.eventCode,
                    eventName: newItem.eventName !== undefined ? newItem.eventName : existingItem.eventName,
                    starterSupported: newItem.starterSupported !== undefined ? newItem.starterSupported : existingItem.starterSupported,
                    starterDisabled: newItem.starterDisabled !== undefined ? newItem.starterDisabled : existingItem.starterDisabled,
                    longitude: longitude,
                    latitude: latitude,
                    engineOdometer: newItem.engineOdometer !== undefined && Number(newItem.engineOdometer).toFixed(2) || existingItem.engineOdometer !== undefined && Number(existingItem.engineOdometer).toFixed(2) || 0,
                    ecuEngineOdometer: newItem.ecuEngineOdometer !== undefined && Number(newItem.ecuEngineOdometer).toFixed(2) || existingItem.ecuEngineOdometer !== undefined && Number(existingItem.ecuEngineOdometer).toFixed(2) || 0,
                    engineHours: newItem.engineHours !== undefined && Number(newItem.engineHours).toFixed(2) || existingItem.engineHours !== undefined && Number(existingItem.engineHours).toFixed(2) || 0,
                    ecuEngineHours: newItem.ecuEngineHours !== undefined && Number(newItem.ecuEngineHours).toFixed(2) || existingItem.ecuEngineHours !== undefined && Number(existingItem.ecuEngineHours).toFixed(2) || 0,
                    dtc: newItem.dtc !== undefined && newItem.dtc !== "null" && newItem.dtc || existingItem.dtc,
                    deviceLabel: newItem.deviceLabel !== undefined ? newItem.deviceLabel : existingItem.deviceLabel,
                    heading: newItem.heading !== undefined ? newItem.heading : existingItem.heading,
                    address: newItem.address !== undefined ? getAddress(newItem.address) : getAddress(existingItem.address),
                    desiredStarterState: newItem.desiredStarterState !== undefined ? getDesiredStarterState(newItem.desiredStarterState) : getDesiredStarterState(existingItem.desiredStarterState),
                    serverTime: newItem.serverTime !== undefined ? newItem.serverTime : existingItem.serverTime,
                    landmarkId: newItem.landmarkId !== undefined ? (newItem?.landmarkId || newItem?.landmark_id) : existingItem.landmarkId,
                    landmarkname: newItem.landmarkname !== undefined ? newItem.landmarkname : existingItem.landmarkname,
                    haveLatLng: haveLatLng,
                    changeLatLng: !existingItem?.haveLatLng !== haveLatLng && haveLatLng,
                    temperatures: newItem.temperatures !== undefined ? getTemperatures(newItem.temperatures) : getTemperatures(existingItem.temperatures),
                    deviceData: newItem.deviceData !== undefined ? newItem.deviceData : existingItem.deviceData,
                    speed,
                    ecuSpeed,
                    unitType: newItem.unitType !== undefined ? newItem.unitType : existingItem.unitType,
                    trail: newItem?.trail && Array.isArray(newItem.trail) ? newItem.trail.map((t) => { return { ...t, lng: t.lon, isPlayed: false } }) : existingItem?.trail || [],
                    rowId: newItem?.rowId || 0,
                    startingLoad: newItem?.startingLoad !== undefined ? newItem?.startingLoad : existingItem?.startingLoad,
                    speedLimit: newItem?.speedLimit !== undefined ? newItem?.speedLimit : existingItem?.speedLimit,
                    LastMessageTime: newItem?.LastMessageTime !== undefined ? newItem?.LastMessageTime : existingItem?.LastMessageTime
                };

                validUpdates.push(mergedItem);
            }

            // Push all the valid computed objects into RTK adapter so they are merged natively and reactively
            if (validUpdates.length > 0) {
                lastReadingAdapter.upsertMany(state, validUpdates);
            }
        },
        removeLastReadings: lastReadingAdapter.removeMany,
        clearLastReadings: lastReadingAdapter.removeAll,
        addLastReadings: lastReadingAdapter.addMany,
    }
});

// Helper for side effects: Mutating sessionStorage directly within the true Redux reducer breaks purity.
// It is recommended to call this function within the RTK Listener Middleware or Thunk right after triggering the Redux action.
export const captureDiferentialUnitsSideEffect = (items) => {
    try {
        let payloadItems = Array.isArray(items) ? items : [items];
        let unitsDiferential = sessionStorage.getItem("unitsDiferential");
        unitsDiferential = unitsDiferential && JSON.parse(unitsDiferential) || [];

        for (let objItem of payloadItems) {
            const deviceId = objItem.deviceId || objItem.id;
            let unitDiferentialIndex = unitsDiferential.findIndex(x => x.deviceId === deviceId);
            if (unitDiferentialIndex > -1) {
                unitsDiferential[unitDiferentialIndex] = { deviceId: deviceId, unitTime: objItem.unitTime };
            } else {
                unitsDiferential.push({ deviceId: deviceId, unitTime: objItem.unitTime });
            }
        }

        sessionStorage.setItem("unitsDiferential", JSON.stringify(unitsDiferential));
    } catch (err) {
        console.error("Failed to capture diferential units", err);
    }
};

export const { upsertManyInternal, removeLastReadings, clearLastReadings, addLastReadings } = lastReadingSlice.actions;

export const upsertManyWithRules = (items) => (dispatch, getState) => {
    if (!items || !items.length) return;

    const state = getState();
    const unitEntities = state.realtime?.unit?.entities || {};

    const validItems = items.filter(newItemRaw => {
        const deviceId = newItemRaw.deviceId || newItemRaw.id;

        // Validation: Verify if the underlying unit exists first
        if (!unitEntities[deviceId]) {
            LogManagerConsole("LastReading-Rejected", "**** Rejected Socket Reading: (Unit not found)", { deviceId, newItemRaw });
            return false;
        }

        return true;
    });

    if (validItems.length > 0) {
        dispatch(upsertManyInternal(validItems));
    }
};

// Create generic selectors where `state.realtime.lastReadings` will be configured in store
export const lastReadingSelectors = lastReadingAdapter.getSelectors(state => state.realtime.lastReading);

export default lastReadingSlice.reducer;
